import { Injectable } from '@angular/core';
import { from, Observable, map } from 'rxjs';
import { supabase } from '../../core/supabase/supabase.client';

export interface ThemeConfig {
  // Basic Colors
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  surfaceColor: string;
  headingColor: string;
  textColor: string;
  placeholderColor: string;
  borderColor: string;

  // Header Theme Settings
  headerBackgroundType: 'solid' | 'gradient';
  headerColorStart: string;
  headerColorEnd: string;
  headerTextColor: string;
  headerSubTextColor: string;
  searchBarBackground: string;
  searchIconColor: string;
  searchPlaceholderColor: string;
  statusBarStyle: 'dark-content' | 'light-content';
}

export interface AppTheme {
  id: string;
  name: string;
  status: ThemeStatus;
  /** @deprecated Theme availability is controlled by status. */
  is_active?: boolean;
  theme_config: ThemeConfig;
  created_at?: string;
  updated_at?: string;
}

export type ThemeStatus = 'draft' | 'published';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly TABLE = 'app_themes';

  getThemes(): Observable<AppTheme[]> {
    return from(
      supabase
        .from(this.TABLE)
        .select('*')
        .order('created_at', { ascending: false })
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data as AppTheme[];
      })
    );
  }

  getThemeById(id: string): Observable<AppTheme> {
    return from(
      supabase.from(this.TABLE).select('*').eq('id', id).single()
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data as AppTheme;
      })
    );
  }

  saveTheme(theme: Partial<AppTheme>): Observable<AppTheme> {
    const themeId = theme.id || crypto.randomUUID();

    const payload = {
      id: themeId,
      name: theme.name,
      theme_config: theme.theme_config,
      status: theme.status ?? 'draft',
      is_active: false,
      updated_at: new Date().toISOString(),
    };

    return from(
      supabase.from(this.TABLE).upsert(payload).select().single()
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data as AppTheme;
      })
    );
  }

  deleteTheme(id: string): Observable<void> {
    return from(supabase.from(this.TABLE).delete().eq('id', id)).pipe(
      map(({ error }) => {
        if (error) throw error;
      })
    );
  }

  setThemeStatus(id: string, status: ThemeStatus): Observable<AppTheme> {
    return from(
      supabase
        .from(this.TABLE)
        .update({
          status,
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single()
    ).pipe(
      map(({ data, error }) => {
        if (error) throw error;
        return data as AppTheme;
      })
    );
  }

  getDefaultConfig(): ThemeConfig {
    return {
      primaryColor: '#248842',
      secondaryColor: '#F8C516',
      backgroundColor: '#F7FCF8',
      surfaceColor: '#FFFFFF',
      headingColor: '#111827',
      textColor: '#374151',
      placeholderColor: '#9CA3AF',
      borderColor: '#E5E7EB',
      headerBackgroundType: 'gradient',
      headerColorStart: '#EAF8EE',
      headerColorEnd: '#F7FCF8',
      headerTextColor: '#111827',
      headerSubTextColor: '#374151',
      searchBarBackground: '#FFFFFF',
      searchIconColor: '#248842',
      searchPlaceholderColor: '#9CA3AF',
      statusBarStyle: 'dark-content',
    };
  }
}
